import dayjs from "dayjs"
import type {
  StopSearchAction,
  ResetSearchAction,
  SetMeetingPointsAction,
  SetSpotTakenAction,
  SetIsDoneSearchingAction,
  SetIsOutOfServiceAreaAction,
  SetSchedulerFetchErrorAction,
  SetIsSearchingAction,
  SetSlotsGetSuccessAction,
  SetSearchDatesAction,
  SetIncludePrivateAction,
  SetSelectedLessonTypeAction,
} from "../actions/searchActions"
import {
  RESET_SEARCH,
  SLOTS_GET_SUCCESS,
  SCHEDULE_FETCH_ERROR,
  OUT_OF_SERVICE_AREA,
  IS_SEARCHING,
  STOP_SEARCH,
  IS_DONE_SEARCHING,
  SET_SEARCH_DATES,
  SET_MEETING_POINTS,
  SET_SPOT_TAKEN,
  SET_INCLUDE_PRIVATE,
  SET_SELECTED_LESSON_TYPE,
} from "../actions/types"
import { DAYS_AT_A_TIME } from "../config"
import { lessonTypes } from "../content"
import { AddressPoint, LessonType, SlotsData } from "../types"

interface ReduxSearchState {
  slots: SlotsData | null
  errors: string | null
  outOfServiceArea: boolean
  startDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
  isSearching: boolean
  isDoneSearch: boolean // separate from isSearching because it indicates we did one.
  isStopping: boolean
  meetingPoints: AddressPoint[] | null
  isSpotTaken: boolean
  selectedLessonType: LessonType
  includePrivate: boolean
}

const INITIAL_STATE: ReduxSearchState = {
  slots: null,
  errors: null,
  outOfServiceArea: false,
  startDate: dayjs().startOf("day"),
  endDate: dayjs().add(DAYS_AT_A_TIME, "days").endOf("day"),
  isSearching: false,
  isDoneSearch: false,
  isStopping: false,
  meetingPoints: null,
  isSpotTaken: false,
  selectedLessonType: lessonTypes.standard,
  includePrivate: false,
}

type Actions =
  | StopSearchAction
  | ResetSearchAction
  | SetSelectedLessonTypeAction
  | SetMeetingPointsAction
  | SetSpotTakenAction
  | SetIsDoneSearchingAction
  | SetIsOutOfServiceAreaAction
  | SetSchedulerFetchErrorAction
  | SetIsSearchingAction
  | SetSlotsGetSuccessAction
  | SetSearchDatesAction
  | SetIncludePrivateAction

export default (state = INITIAL_STATE, action: Actions): ReduxSearchState => {
  switch (action.type) {
    case SLOTS_GET_SUCCESS:
      return {
        ...state,
        slots: { ...state.slots, ...action.payload },
      }
    case RESET_SEARCH:
      return {
        ...INITIAL_STATE,
        includePrivate: state.includePrivate,
        // Don't reset lesson type, as we want to continue to search for whatever
        // was selected.
        selectedLessonType: state.selectedLessonType,
      }
    case SCHEDULE_FETCH_ERROR:
      return { ...state, errors: action.payload }
    case OUT_OF_SERVICE_AREA:
      return { ...state, outOfServiceArea: true }
    case IS_SEARCHING:
      return { ...state, isSearching: action.payload }
    case STOP_SEARCH:
      return { ...state, isStopping: action.payload }
    case SET_SEARCH_DATES:
      return {
        ...state,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      }
    case SET_MEETING_POINTS:
      return { ...state, meetingPoints: action.payload }
    case SET_SPOT_TAKEN:
      return { ...state, isSpotTaken: action.payload }
    case IS_DONE_SEARCHING:
      return { ...state, isDoneSearch: action.payload }
    case SET_INCLUDE_PRIVATE:
      return { ...state, includePrivate: action.payload }
    case SET_SELECTED_LESSON_TYPE:
      return { ...state, selectedLessonType: action.payload }
    default:
      return state
  }
}
